import { Select, Input, Button as AntButton, Space, Tooltip } from "antd";
import {
  ExportOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ButtonType,
  Col,
  Row,
} from "syngenta-digital-cropwise-react-ui-kit";
import {
  DeleteConfirmationPopup,
  NotificationType,
  openNotificationWithIcon,
} from "../../shared/components/ConfirmationsAndNotifiCations";
import { GlobalContext } from "../../context/AppContextProvider";
import { FETCH_ASSESSMENT_BY_ID_SUCCESS } from "../../context/actions/ActionTypes";
import { fetchAssessmentById } from "../../context/actions/Assessment";
import { AssessmentStepsArray } from "../../constants/Assesments";
import { ErrorDisplay } from "../../shared/components/Error";
import { Loader } from "../../shared/components/Loader";
import { TitleAndSubTitle } from "../../shared/components/Typography";
import AxiosInstance from "../../shared/utils/axios";
import { type PaginatedAssesments, type GenericMetadataKey } from "./types";
import { datadogAddAction } from "@cw-elements/analytics-provider";
import { goToStepPage } from "../commonMethods";
import debounce from "lodash/debounce";
import {
  AssessmentStepDisplayNames,
  trackAssessmentCloneClicked,
  trackAssessmentDeleteClicked,
  trackAssessmentEditClicked,
  trackAssessmentReportClicked,
  trackAssessmentsFilterApplied,
  trackCreateNewAssessmentClicked,
} from "../../shared/utils/analyticsEvents";
import "./styles.less";
import { withWorkspaceId } from "shared/utils/workspaceRouting";

const statusStyles: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  indraft: { label: "DRAFT", bg: "#ffe4ae", color: "#744a0b" },
  inprogress: { label: "IN PROGRESS", bg: "#c6e6ff", color: "#005693" },
  completed: { label: "COMPLETED", bg: "#c3ead1", color: "#0c612c" },
  error: { label: "ERROR", bg: "#fff1f0", color: "#a8071a" },
};

export const Assessments = (): ReactElement => {
  const navigate = useNavigate();
  const { orgId = "" } = useParams();
  const {
    assessmentStepsDispatch,
    appGenericMetadata,
    setAppGenericMetadata,
    assessmentDispatch,
    orgState,
  } = useContext(GlobalContext);
  const [assessmentsData, setAssessmentsData] = useState<PaginatedAssesments>({
    assessments: [],
    totalDocs: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedYear, setSelectedYear] = useState<number | null>(
    dayjs().year()
  );
  const [searchText, setSearchText] = useState<string>("");
  const { t } = useTranslation();
  const onCreateAssessment = (
    buttonPlacement: "Bottom Center" | "Top Right"
  ): void => {
    trackCreateNewAssessmentClicked({
      workspaceId: orgState?.currentOrg?.workspace_id,
      organizationName: orgState?.currentOrg?.name,
      buttonPlacement,
    });
    datadogAddAction("New Assessment Button Clicked", {
      productId: "product-123",
      category: "new assessment",
    });
    navigate(
      withWorkspaceId(
        `/app/organization/${orgId}/assessment/create/custom/basic-info`
      )
    );
  };

  const yearOptions = useMemo(() => {
    const years = Array.from(
      new Set(assessmentsData.assessments.map((a) => dayjs(a.createdAt).year()))
    ).sort((a, b) => b - a);
    return [
      { value: null, label: t("All Years") },
      ...years.map((year) => ({ value: year, label: year.toString() })),
    ];
  }, [assessmentsData.assessments, t]);

  const getAllAssessments = async (): Promise<void> => {
    try {
      setLoading(true);
      const requestURL = `/api/assessments/list?orgId=${orgId}&page=1&limit=1000&sortType=desc&sortBy=createdAt`;
      const payload = { cropCycleIds: [], status: [] };
      const { data } = await AxiosInstance.post(requestURL, payload);
      setAssessmentsData({
        assessments: data.docs,
        totalDocs: data.totalDocs,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getAllPageGenericMetaData = async (
    orgIdLocal: string
  ): Promise<void> => {
    if (!orgIdLocal) return;
    const fetchAndSet = async (
      key: GenericMetadataKey,
      url: string
    ): Promise<void> => {
      if (!appGenericMetadata?.has(key)) {
        const response = await AxiosInstance.get(url);
        setAppGenericMetadata?.(appGenericMetadata.set(key, response?.data));
      }
    };
    await fetchAndSet("providers", "/api/providers");
    await fetchAndSet(
      "applications",
      "/api/generic-metadata?page=applications"
    );
    await fetchAndSet("practices", "/api/generic-metadata?page=practices");
    await fetchAndSet(
      "soil-characteristics",
      "/api/generic-metadata?page=soil-characteristics"
    );
    await fetchAndSet("yield", "/api/generic-metadata?page=yield");
  };

  useEffect(() => {
    assessmentDispatch({ type: FETCH_ASSESSMENT_BY_ID_SUCCESS, payload: {} });
  }, []);

  useEffect(() => {
    if (orgId) {
      void getAllAssessments();
      void getAllPageGenericMetaData(orgId);
    } else {
      setLoading(false);
    }
  }, [orgId]);

  const filteredAssessments = assessmentsData.assessments
    .filter(
      (a) => selectedYear === null || dayjs(a.createdAt).year() === selectedYear
    )
    .filter((a) => {
      if (!searchText) return true;
      const txt = [
        a.assessmentName,
        a.cropName,
        a.cropCycleName,
        (a.provider || []).join(" "),
        a.assessmentStatus,
      ]
        .join(" ")
        .toLowerCase();
      return txt.includes(searchText.toLowerCase());
    });

  const formatDate = (d: string): string => dayjs(d).format("D MMM YYYY");

  const generatePDFHandler = (
    assessment: PaginatedAssesments["assessments"][number]
  ): void => {
    setLoading(true);
    AxiosInstance.get(`/api/pdf-report?assessmentId=${assessment.assessmentId}`)
      .then(({ data }) => {
        const PDFurl: string = data.url;
        if (window.open(PDFurl, "_blank") === null) {
          throw new Error("Unable to open PDF report");
        }
        trackAssessmentReportClicked({
          assessmentName: assessment.assessmentName,
          crop: assessment.cropName,
          status: assessment.assessmentStatus,
          actionResult: "PDF Viewed",
        });
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        trackAssessmentReportClicked({
          assessmentName: assessment.assessmentName,
          crop: assessment.cropName,
          status: assessment.assessmentStatus,
          actionResult: "Error Displayed",
        });
      });
  };

  const handleEditClick = async (
    assessmentId: string,
    cardItem: PaginatedAssesments["assessments"][number]
  ): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await fetchAssessmentById(
        assessmentDispatch,
        assessmentId
      );
      const stepPageIndex = AssessmentStepsArray.indexOf(data.currentPage);
      if (data.currentPage && stepPageIndex >= 0) {
        trackAssessmentEditClicked({
          assessmentName: cardItem.assessmentName,
          crop: cardItem.cropName,
          status: cardItem.assessmentStatus,
          stepName: AssessmentStepDisplayNames[data.currentPage],
        });
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const url = `/app/organization/${orgId}/assessment/edit/${assessmentId}/custom/${data.currentPage}?isNewAssessment=false`;
        goToStepPage({
          navigate,
          assessmentStepsDispatch,
          url,
          activeStep: stepPageIndex,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyAssessmentDeletion = (assessmentId: string): void => {
    setAssessmentsData((prev) => ({
      assessments: prev.assessments.filter(
        (a) => a.assessmentId !== assessmentId
      ),
      totalDocs: Math.max(0, prev.totalDocs - 1),
    }));
  };

  const performDelete = async (assessmentId: string): Promise<void> => {
    setLoading(true);
    try {
      await AxiosInstance.delete(`/api/assessments/${assessmentId}`);
      applyAssessmentDeletion(assessmentId);
      openNotificationWithIcon({
        type: NotificationType.SUCCESS,
        message: "Assessment deleted successfully!",
      });
    } catch (error) {
      openNotificationWithIcon({
        type: NotificationType.ERROR,
        message: "Error deleting assessment",
        description:
          "An error occurred while deleting the assessment. Please try again.",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteHandler = (
    assessmentId: string,
    assessmentName?: string
  ): void => {
    DeleteConfirmationPopup({
      title: "Do you want to delete this assessment?",
      content: "This action cannot be undone.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => {
        trackAssessmentDeleteClicked({ assessmentName, actionTaken: "Delete" });
        void performDelete(assessmentId);
      },
      onCancel: () => {
        trackAssessmentDeleteClicked({ assessmentName, actionTaken: "Cancel" });
      },
    });
  };

  const cloneHandler = (
    id: string,
    originalAssessmentName?: string,
    crop?: string,
    status?: string
  ): void => {
    AxiosInstance.post(`/api/assessments/${id}/copy`)
      .then(({ data }) => {
        setLoading(false);
        const newAssessmentId: string = data.assessmentId;
        trackAssessmentCloneClicked({
          originalAssessmentName,
          newAssessmentName: data.assessmentName,
          crop,
          status: status ?? "",
        });
        const url = `/app/organization/${orgId}/assessment/edit/${newAssessmentId}/custom/basic-info?isNewAssessment=false`;
        openNotificationWithIcon({
          type: NotificationType.SUCCESS,
          message: "Assessment cloned successfully!",
        });
        goToStepPage({
          navigate,
          assessmentStepsDispatch,
          url,
          activeStep: 0,
        });
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  const debouncedTrackSearch = useRef(
    debounce((value: string) => {
      trackAssessmentsFilterApplied({
        filterType: "Search Box",
        selectedValue: Boolean(value),
      });
    }, 500)
  ).current;

  const debouncedCloneHandler = useRef(
    debounce(
      (
        id: string,
        originalAssessmentName?: string,
        crop?: string,
        status?: string
      ) => {
        cloneHandler(id, originalAssessmentName, crop, status);
      },
      1000
    )
  ).current;

  return (
    <>
      {loading ? (
        <Loader mask />
      ) : (
        <>
          <div className="assessments-header">
            <TitleAndSubTitle titleFontSize={28} title={t("My Assessments")} />
            <Button
              type={ButtonType.primary}
              onClick={() => {
                onCreateAssessment("Top Right");
              }}
              className="assessments-create-btn"
            >
              {t("+  Create new assessment")}
            </Button>
          </div>
          <Row gutter={16}>
            {assessmentsData.assessments.length === 0 &&
            assessmentsData.totalDocs === 0 ? (
              <Col span={24}>
                <ErrorDisplay
                  title="No assessments yet"
                  subTitle={t(
                    "Create a new assessment for you to be\nable to review your reports."
                  )}
                  cardClassName="custom-card create-new-asmt-btn"
                >
                  <Button
                    type={ButtonType.primary}
                    onClick={() => {
                      onCreateAssessment("Bottom Center");
                    }}
                    className="assessments-empty-create-btn"
                  >
                    {t("Create new assessment")}
                  </Button>
                </ErrorDisplay>
              </Col>
            ) : (
              <Col sm={24} md={24} lg={24} className="assessments-wrapper">
                <div className="assessments-filters">
                  <div className="assessments-year-wrapper">
                    <TitleAndSubTitle titleFontSize={20} title={t("Year")} />
                    <Select
                      size="middle"
                      value={selectedYear}
                      onChange={(yearValue) => {
                        setSelectedYear(yearValue);
                        trackAssessmentsFilterApplied({
                          filterType: "Year",
                          selectedValue: yearValue ?? "All Years",
                        });
                      }}
                      options={yearOptions}
                      className="assessments-year-select"
                      placeholder={t("Year")}
                    />
                  </div>
                  <Input
                    allowClear
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchText(e.target.value);
                      debouncedTrackSearch(e.target.value);
                    }}
                    placeholder={t("Search assessments")}
                    className="assessments-search"
                  />
                </div>

                <div className="assessments-grid">
                  {filteredAssessments.map((item) => {
                    const key = item.assessmentStatus.toLowerCase();
                    const meta = statusStyles[key];
                    const errorMessage = item.genericData?.error?.message;
                    return (
                      <div key={item.assessmentId} className="assessment-card">
                        <div className="assessment-left">
                          <div className="assessment-name">
                            {item.assessmentName ?? t("No Assessment Name")}
                          </div>
                          <div className="assessment-date">
                            {formatDate(item.createdAt)}
                          </div>
                          <div className="assessment-status-wrapper">
                            <span
                              className="assessment-status-badge"
                              style={{
                                background: meta?.bg ?? "#ffffff",
                                color: meta?.color ?? "#000000",
                              }}
                            >
                              {meta.label}
                            </span>
                            {key === "error" && errorMessage && (
                              <Tooltip title={errorMessage} placement="top">
                                <InfoCircleOutlined
                                  className="assessment-error-icon"
                                  style={{ color: meta.color }}
                                />
                              </Tooltip>
                            )}
                          </div>
                          <div className="assessment-crop">
                            {t("Crop")}: {item.cropName || "-"}
                          </div>
                        </div>
                        <div className="assessment-actions">
                          <Space
                            direction="vertical"
                            size={4}
                            className="assessment-actions-space"
                          >
                            <AntButton
                              type="link"
                              icon={<ExportOutlined />}
                              className="assessment-action-btn"
                              onClick={() => {
                                if (key === "completed") {
                                  generatePDFHandler(item);
                                } else {
                                  trackAssessmentReportClicked({
                                    assessmentName: item.assessmentName,
                                    crop: item.cropName,
                                    status: item.assessmentStatus,
                                    actionResult: "Error Displayed",
                                  });
                                  openNotificationWithIcon({
                                    type: NotificationType.ERROR,
                                    message: t("Report unavailable"),
                                    description: t(
                                      "Assessment is not completed yet."
                                    ),
                                  });
                                }
                              }}
                            >
                              {t("Report")}
                            </AntButton>
                            <AntButton
                              type="link"
                              icon={<EditOutlined />}
                              className="assessment-action-btn"
                              disabled={key !== "indraft"}
                              onClick={() => {
                                void handleEditClick(item.assessmentId, item);
                              }}
                            >
                              {t("Edit")}
                            </AntButton>
                            <AntButton
                              type="link"
                              icon={<CopyOutlined />}
                              className="assessment-action-btn"
                              onClick={() =>
                                debouncedCloneHandler(
                                  item.assessmentId,
                                  item.assessmentName,
                                  item.cropName,
                                  item.assessmentStatus
                                )
                              }
                            >
                              {t("Clone")}
                            </AntButton>
                            <AntButton
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              className="assessment-action-btn assessment-action-delete"
                              onClick={() => {
                                deleteHandler(
                                  item.assessmentId,
                                  item.assessmentName
                                );
                              }}
                            >
                              {t("Delete")}
                            </AntButton>
                          </Space>
                        </div>
                      </div>
                    );
                  })}

                  {filteredAssessments.length === 0 && (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        textAlign: "center",
                        padding: "60px 0",
                        color: "#666",
                        fontSize: 18,
                      }}
                    >
                      {t("No assessments to display")}
                    </div>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </>
      )}
    </>
  );
};
